import { Controller } from "@hotwired/stimulus"

// Lädt parallel zum aktuell angezeigten Stream-Item den nächsten Content (Bild oder Video)
// vor und dispatcht ein "ready" Event, sobald das Medium anzeigefertig ist.
//
// Verwendet Blob URLs statt HTML-Medienelemente zu erstellen, um doppelte Fetches zu vermeiden.
export default class extends Controller {
  static values = {
    url: String,
    threshold: { type: Number, default: 20 },
    timeout: { type: Number, default: 30000 }
  }

  connect() {
    this.boundHandleIncoming = (e) => this.#handleIncoming(e)
    document.addEventListener("stream:incoming", this.boundHandleIncoming)
    this.preload()
  }

  preload() {
    this.#clearPending()
    this.#revokeBlobUrl()

    fetch(this.urlValue, { headers: { Accept: "application/json" } })
      .then(response => {
        if (!response.ok || response.status === 204) return null
        return response.json()
      })
      .then(data => {
        if (!data || !data.url) return
        this.#loadContent(data)
      })
      .catch(error => console.warn("Stream preload failed:", error))
  }

  disconnect() {
    document.removeEventListener("stream:incoming", this.boundHandleIncoming)
    this.#clearPending()
    this.#revokeBlobUrl()
  }

  #handleIncoming(event) {
    this.#clearPending()
    this.#revokeBlobUrl()
    this.#loadContent(event.detail)
  }

  #revokeBlobUrl() {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl)
      this.currentBlobUrl = null
    }
  }

  #loadContent(data) {
    if (data.type === "image") {
      this.#fetchImage(data)
    } else if (data.type === "video") {
      this.#fetchVideo(data)
    }
  }

  #fetchImage(data) {
    fetch(data.url, { mode: "cors" })
      .then(response => {
        if (!response.ok) throw new Error("Image fetch failed")
        return response.blob()
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        this.currentBlobUrl = blobUrl
        this.#dispatchReady({ ...data, blob_url: blobUrl })
      })
      .catch(() => {
        this.#dispatchReady(data)
      })

    this.timeoutId = setTimeout(() => this.#dispatchReady(data), this.timeoutValue)
  }

  #fetchVideo(data) {
    fetch(data.url, { mode: "cors" })
      .then(response => {
        if (!response.ok) throw new Error("Video fetch failed")
        return response.blob()
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        this.currentBlobUrl = blobUrl
        this.#dispatchReady({ ...data, blob_url: blobUrl })
      })
      .catch(() => {
        this.#dispatchReady(data)
      })

    this.timeoutId = setTimeout(() => this.#dispatchReady(data), this.timeoutValue)
  }

  #dispatchReady(data) {
    if (this.dispatched) return
    this.dispatched = true
    this.#clearPending()
    if (data.blob_url) this.currentBlobUrl = null
    this.dispatch("ready", { detail: data })
  }

  #clearPending() {
    clearTimeout(this.timeoutId)
    this.timeoutId = null
    this.dispatched = false
  }
}